import {
  formatScheduledForDisplay,
  formatScheduledRelative,
  getSendPipelinePhase,
  nextAutoMaterializeCronMs,
  type SendPipelinePhase,
} from './newsletterSchedule';
import { newsletterStatusKey, normalizeNewsletterStatus } from './newsletterStatus';

export type NewsletterDashboardRow = {
  id: string;
  name: string;
  status: string;
  scheduled_for: string | null;
  subtitle: string | null;
  channels: unknown;
};

export type DashboardActionKind =
  | 'commit_send'
  | 'queue_recipients'
  | 'add_send_date'
  | 'review_queued_send'
  | 'plan_distribution'
  | 'linkedin_window'
  | 'on_hold_review';

export type DashboardAction = {
  kind: DashboardActionKind;
  label: string;
  detail: string;
  priority: number;
};

export type DashboardBucket =
  | 'draft_no_date'
  | 'draft_planned'
  | 'draft_overdue'
  | 'scheduled_not_queued'
  | 'scheduled_overdue'
  | 'queued_upcoming'
  | 'queued_overdue'
  | 'on_hold'
  | 'published_recent'
  | 'other';

export type ClassifiedNewsletter = {
  row: NewsletterDashboardRow;
  phase: SendPipelinePhase;
  bucket: DashboardBucket;
  sendMs: number | null;
  daysUntilSend: number | null;
  actions: DashboardAction[];
};

const MS_PER_DAY = 86_400_000;

function parseSendMs(scheduledFor: string | null): number | null {
  if (!scheduledFor?.trim()) return null;
  const ms = new Date(scheduledFor.trim()).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function daysUntil(sendMs: number | null, nowMs: number): number | null {
  if (sendMs == null) return null;
  return Math.round((sendMs - nowMs) / MS_PER_DAY);
}

function bucketFor(phase: SendPipelinePhase, status: string, sendMs: number | null, nowMs: number): DashboardBucket {
  const s = newsletterStatusKey(status);
  if (s === 'on_hold') return 'on_hold';
  if (phase === 'sent') {
    if (sendMs != null && sendMs >= nowMs - 14 * MS_PER_DAY) return 'published_recent';
    return 'other';
  }
  if (phase === 'draft_planned') return 'draft_planned';
  if (phase === 'draft_overdue') return 'draft_overdue';
  if (phase === 'planned') return 'scheduled_not_queued';
  if (phase === 'overdue_planned') return 'scheduled_overdue';
  if (phase === 'queued') {
    if (sendMs != null && sendMs < nowMs) return 'queued_overdue';
    return 'queued_upcoming';
  }
  if (phase === 'no_date' && (s === 'draft' || !s)) return 'draft_no_date';
  return 'other';
}

function buildActions(
  phase: SendPipelinePhase,
  status: string,
  sendMs: number | null,
  days: number | null,
  nowMs: number,
): DashboardAction[] {
  const actions: DashboardAction[] = [];
  const when = sendMs != null ? formatScheduledForDisplay(new Date(sendMs).toISOString()) : null;
  const relative = sendMs != null ? formatScheduledRelative(new Date(sendMs).toISOString(), nowMs) : null;

  if (phase === 'no_date' && newsletterStatusKey(status) !== 'on hold') {
    actions.push({
      kind: 'add_send_date',
      label: 'Set planned send date',
      detail: 'Pick a send date in Settings before committing or queuing.',
      priority: 50,
    });
  }

  if (phase === 'draft_planned' || phase === 'draft_overdue') {
    actions.push({
      kind: 'commit_send',
      label: 'Set status to Scheduled',
      detail: phase === 'draft_overdue'
        ? `Send date was ${when ?? 'in the past'} — commit or move the date before you queue.`
        : `Send ${relative ?? when ?? 'soon'} — status is still Draft (planning only).`,
      priority: phase === 'draft_overdue' ? 5 : days != null && days <= 7 ? 15 : 40,
    });
    if (days != null && days >= 3 && days <= 21) {
      actions.push({
        kind: 'plan_distribution',
        label: 'Plan LinkedIn / site rollout',
        detail: 'Draft feed post and comment from the canonical piece (distribute skill). Site stays canonical.',
        priority: 35,
      });
    }
  }

  if (phase === 'planned' || phase === 'overdue_planned') {
    const nextCron = formatScheduledForDisplay(new Date(nextAutoMaterializeCronMs(nowMs)).toISOString());
    actions.push({
      kind: 'queue_recipients',
      label: 'Queue recipients',
      detail:
        phase === 'overdue_planned'
          ? `Overdue — queue now. Auto-queue cron may run at ${nextCron}.`
          : days != null && days <= 1
            ? `Send ${relative ?? 'soon'} — queue manually or wait for auto-queue every 30 minutes (~24h before send).`
            : `Auto-queue opens ~24h before send. Next auto-queue run: ${nextCron}. Or queue manually anytime.`,
      priority: phase === 'overdue_planned' ? 1 : days != null && days <= 2 ? 10 : 25,
    });
  }

  if (phase === 'queued' && sendMs != null) {
    const withinWeek = days != null && days >= 0 && days <= 7;
    actions.push({
      kind: 'review_queued_send',
      label: withinWeek ? 'Review before send' : 'Recipients queued',
      detail: `Sends ${relative ?? when ?? 'at planned time'}. Check Recipients tab; purge only if canceling.`,
      priority: withinWeek ? (days != null && days <= 1 ? 8 : 20) : 60,
    });
  }

  if (
    (phase === 'planned' || phase === 'draft_planned') &&
    days != null &&
    days >= 2 &&
    days <= 10
  ) {
    actions.push({
      kind: 'linkedin_window',
      label: 'LinkedIn promotion window',
      detail: `Ideal ~3–7 days before send (${when}). One feed post → canonical URL; newsletter goes deeper.`,
      priority: 30,
    });
  }

  if (newsletterStatusKey(status) === 'on_hold') {
    actions.push({
      kind: 'on_hold_review',
      label: 'Resume or reschedule',
      detail: 'On hold — nothing auto-queues or sends until status is Draft or Scheduled.',
      priority: 45,
    });
  }

  return actions.sort((a, b) => a.priority - b.priority);
}

export function classifyNewsletter(row: NewsletterDashboardRow, nowMs = Date.now()): ClassifiedNewsletter {
  const phase = getSendPipelinePhase({
    status: row.status,
    scheduledFor: row.scheduled_for,
    hasRecipientRows: normalizeNewsletterStatus(row.status) === 'ready_to_publish',
  });
  const sendMs = parseSendMs(row.scheduled_for);
  const days = daysUntil(sendMs, nowMs);
  const bucket = bucketFor(phase, row.status, sendMs, nowMs);

  return {
    row,
    phase,
    bucket,
    sendMs,
    daysUntilSend: days,
    actions: buildActions(phase, row.status, sendMs, days, nowMs),
  };
}

export function classifyNewsletters(rows: NewsletterDashboardRow[], nowMs = Date.now()): ClassifiedNewsletter[] {
  return rows
    .map((row) => classifyNewsletter(row, nowMs))
    .sort((a, b) => {
      const aMs = a.sendMs ?? Number.MAX_SAFE_INTEGER;
      const bMs = b.sendMs ?? Number.MAX_SAFE_INTEGER;
      if (aMs !== bMs) return aMs - bMs;
      return a.row.name.localeCompare(b.row.name, undefined, { sensitivity: 'base' });
    });
}

export type DashboardSummary = {
  draftNoDate: number;
  draftPlanned: number;
  draftOverdue: number;
  scheduledNotQueued: number;
  scheduledOverdue: number;
  queuedNext7: number;
  onHold: number;
  horizon30: number;
};

export function summarizeDashboard(items: ClassifiedNewsletter[], nowMs = Date.now()): DashboardSummary {
  const in7 = (item: ClassifiedNewsletter) =>
    item.sendMs != null && item.sendMs >= nowMs && item.sendMs <= nowMs + 7 * MS_PER_DAY;
  const in30 = (item: ClassifiedNewsletter) =>
    item.sendMs != null && item.sendMs >= nowMs && item.sendMs <= nowMs + 30 * MS_PER_DAY;

  return {
    draftNoDate: items.filter((i) => i.bucket === 'draft_no_date').length,
    draftPlanned: items.filter((i) => i.bucket === 'draft_planned').length,
    draftOverdue: items.filter((i) => i.bucket === 'draft_overdue').length,
    scheduledNotQueued: items.filter((i) => i.bucket === 'scheduled_not_queued').length,
    scheduledOverdue: items.filter((i) => i.bucket === 'scheduled_overdue').length,
    queuedNext7: items.filter((i) => i.bucket === 'queued_upcoming' && in7(i)).length,
    onHold: items.filter((i) => i.bucket === 'on_hold').length,
    horizon30: items.filter((i) => in30(i) && i.bucket !== 'published_recent').length,
  };
}

/** Flat action list for the next 7 days (and overdue), highest priority first. */
export function actionsNext7Days(items: ClassifiedNewsletter[], nowMs = Date.now()): Array<{
  item: ClassifiedNewsletter;
  action: DashboardAction;
}> {
  const cutoff = nowMs + 7 * MS_PER_DAY;
  const out: Array<{ item: ClassifiedNewsletter; action: DashboardAction }> = [];

  for (const item of items) {
    const overdue = item.sendMs != null && item.sendMs < nowMs;
    const inWindow = item.sendMs != null && item.sendMs <= cutoff;
    if (!overdue && !inWindow && item.bucket !== 'draft_no_date') continue;
    if (item.bucket === 'published_recent' || item.bucket === 'other') continue;

    for (const action of item.actions) {
      if (action.kind === 'plan_distribution' && !inWindow && !overdue) continue;
      if (action.kind === 'linkedin_window' && item.sendMs != null && item.sendMs > cutoff) continue;
      out.push({ item, action });
    }
  }

  return out.sort((a, b) => {
    const pa = a.action.priority;
    const pb = b.action.priority;
    if (pa !== pb) return pa - pb;
    return (a.item.sendMs ?? Number.MAX_SAFE_INTEGER) - (b.item.sendMs ?? Number.MAX_SAFE_INTEGER);
  });
}

export const BUCKET_LABELS: Record<DashboardBucket, string> = {
  draft_no_date: 'Draft — no send date',
  draft_planned: 'Draft — date set, not committed',
  draft_overdue: 'Draft — send date passed',
  scheduled_not_queued: 'Scheduled — not queued yet',
  scheduled_overdue: 'Scheduled — overdue, not queued',
  queued_upcoming: 'Ready to Publish — queued',
  queued_overdue: 'Queued — send time passed',
  on_hold: 'On hold',
  published_recent: 'Published (recent)',
  other: 'Other',
};
